import { LitElement, html, css, unsafeCSS, nothing } from 'lit';
import base from '../styles/base.css?inline';
import { REPORT_OUTLINE, REPORT_TEXT_FACTS, describeRule, factsInText } from '@mv/models';
import { api } from '../api.js';
import { confirmDialog } from '../utils/confirm-dialog.js';
import { formatDate } from '../utils/datetime.js';
import { reportUiStyles } from '../utils/report-ui.js';

/** Paragraafnummer → titel, uit de paragraafboom van het rapport. */
const SECTION_TITLES = new Map();
(function walk(nodes) { for (const n of nodes) { SECTION_TITLES.set(n.key, n.title); walk(n.children || []); } })(REPORT_OUTLINE);

/** Voorbeeldwaarden voor de samenvoegvelden, in de popup en in het voorbeeld van het Word-bestand. */
const MERGE_EXAMPLES = {
  Projectnummer_: 'P2026-001', Projectnaam: 'Voorbeeldlaan 12, Dorpstad', Opdrachtgever: 'Voorbeeld Bouw B.V.', Auteur: 'J. Jansen',
  Auteur_voor_en_achternaam: 'Jan Jansen', Adresregel_1_voor_en_achternaam: 'P. de Vries', Adresregel_2: 'Postbus 123',
  Adresregel_3: '1234 AB Dorpstad', Kenmerk: 'BAV-P2026-001', Versie: '1', Datum: '27 oktober 2026',
  '[adres]': 'Voorbeeldlaan 12, Dorpstad', '[percelen]': 'ABC01-A-1234', '[titel]': 'Verkennend bodemonderzoek Voorbeeldlaan 12, Dorpstad',
  '[onderwerp]': 'Verkennend onderzoek bodem en grondwater',
};
const FIELD_RE = /«([^«»]{1,60})»|(\[(?:adres|percelen|titel|onderwerp)\])/g;

/**
 * Office-lettertypen die een browser meestal niet heeft (Aptos, het themalettertype van Word
 * sinds 2023, en Calibri; Roboto van het GeoVoice-sjabloon). Zonder deze regel valt docx-preview terug op Times. @font-face werkt
 * niet in een shadow root, daarom één keer in de head.
 */
function officeFontFallbacks() {
  if (document.getElementById('gv-office-fonts')) return;
  const alias = (name, ...local) => `@font-face{font-family:"${name}";src:${[name, ...local].map((n) => `local("${n}")`).join(',')};}`
    + `@font-face{font-family:"${name}";font-weight:bold;src:${[`${name} Bold`, ...local.map((n) => `${n} Bold`)].map((n) => `local("${n}")`).join(',')};}`;
  const style = document.createElement('style');
  style.id = 'gv-office-fonts';
  style.textContent = ['Aptos', 'Aptos Display', 'Calibri', 'Calibri Light', 'Roboto']
    .map((f) => alias(f, 'Calibri', 'Segoe UI', 'Helvetica Neue', 'Arial')).join('');
  document.head.append(style);
}

const sectionChapter = (key) => {
  const ch = key.split('.')[0];
  return `${ch} ${SECTION_TITLES.get(ch) || ''}`.trim();
};
const sortSections = (a, b) => a.localeCompare(b, 'nl', { numeric: true });
const n = (k, een, veel) => `${k} ${k === 1 ? een : veel}`;

/**
 * report-settings-page — Instellingen › Rapportage (bèta).
 *
 * Een bureau heeft meerdere rapportsjablonen. Elk sjabloon is twee dingen: de template teksten
 * (vaste teksten en keuzeteksten per paragraaf) en de document opmaak (een eigen Word-bestand,
 * of het standaardformat). Het sjabloon van GeoVoice staat er altijd bij en is niet te wijzigen;
 * dupliceer het om te beginnen.
 *
 * De regel van een keuzetekst (JSON) is techniek: hij staat ingeklapt onder de tekst, en in gewone
 * taal erboven ('Gebruikt als …').
 */
export class ReportSettingsPage extends LitElement {
  static properties = {
    canEdit: { type: Boolean },
    _templates: { state: true },
    _known: { state: true },
    _loading: { state: true },
    _error: { state: true },
    _busy: { state: true },
    _tplId: { state: true },        // open sjabloon; null = overzicht
    _tab: { state: true },          // 'texts' | 'layout'
    _texts: { state: true },
    _textsCanEdit: { state: true },
    _section: { state: true },      // gekozen paragraaf
    _key: { state: true },          // gekozen tekst binnen de paragraaf
    _draft: { state: true },        // { body, rule } van de gekozen tekst
    _filter: { state: true },
    _query: { state: true },
    _uploadOpen: { state: true },
    _dragOver: { state: true },
    _showFields: { state: true },
    _page: { state: true },
    _previewState: { state: true }, // 'loading' | 'ok' | 'error'
    _copied: { state: true },
    _asText: { state: true },       // de gekozen tekst als lopende tekst met voorbeeldwaarden tonen
  };

  static styles = [css`${unsafeCSS(base)}`, reportUiStyles, css`
    :host { display:block; }
    .err { color:var(--mv-danger); font-size:.82rem; font-weight:600; margin:6px 0; }
    .muted { color:var(--mv-text-muted); font-size:.82rem; }
    .card { background:var(--mv-surface); border:1px solid var(--mv-border); border-radius:var(--mv-radius-lg, 14px); box-shadow:var(--mv-shadow-sm); }

    /* Overzicht */
    .ohead { display:flex; align-items:flex-start; gap:14px; flex-wrap:wrap; margin-bottom:28px; }
    .ohead > div { flex:1; min-width:260px; }
    h2 { margin:0; font-size:1.25rem; font-weight:700; letter-spacing:-.02em; color:var(--mv-text); display:flex; align-items:center; gap:8px; }
    .beta { font-size:.6rem; font-weight:700; letter-spacing:.06em; color:var(--mv-info); }
    .ohead p { margin:4px 0 0; font-size:.8125rem; color:var(--mv-text-sub); }
    .grid { display:grid; grid-template-columns:repeat(auto-fill, minmax(300px, 1fr)); gap:18px; }
    .tpl { padding:0; overflow:hidden; cursor:pointer; text-align:left; font:inherit; color:inherit; display:flex; flex-direction:column; transition:border-color .15s, box-shadow .15s; }
    .tpl:hover { border-color:var(--mv-primary); box-shadow:0 8px 22px var(--mv-primary-shadow); }
    .thumb { height:120px; display:flex; justify-content:center; align-items:flex-end; }
    .thumb.green { background:var(--mv-primary-light); } .thumb.blue { background:#EEF3FD; }
    :host-context([data-theme='dark']) .thumb.blue { background:#1C2742; }
    .mini { width:96px; height:96px; background:#fff; border-radius:5px 5px 0 0; box-shadow:0 4px 12px rgba(24,28,50,.1); padding:13px 11px; display:flex; flex-direction:column; gap:6px; }
    .mini i { display:block; height:4px; border-radius:2px; background:#E6EAEE; }
    .mini i.logo { width:26px; height:7px; } .mini i.t { height:4px; width:80%; background:#181C32; }
    .thumb.green .logo { background:var(--mv-primary); } .thumb.blue .logo { background:#1D4ED8; }
    .tbody { padding:16px 18px 18px; display:flex; flex-direction:column; gap:5px; flex:1; }
    .tname { display:flex; align-items:flex-start; gap:8px; justify-content:space-between; }
    .tname b { font-size:.95rem; color:var(--mv-text); }
    .badge { font-size:.6rem; font-weight:700; letter-spacing:.06em; text-transform:uppercase; padding:2px 7px; border-radius:999px; background:var(--mv-primary-tint); color:var(--mv-primary); white-space:nowrap; }
    .badge.gv { background:var(--mv-surface-alt); color:var(--mv-text-sub); }
    .tsub { font-size:.8rem; color:var(--mv-text-sub); }
    .facts { margin-top:8px; border-top:1px solid var(--mv-border-soft); padding-top:10px; display:grid; grid-template-columns:auto 1fr; gap:4px 12px; font-size:.8rem; }
    .facts span:nth-child(odd) { color:var(--mv-text-muted); }
    .facts span:nth-child(even) { color:var(--mv-text); text-align:right; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }

    /* Lege tegel: een extra sjabloon aanmaken, in de vorm van een sjabloontegel. */
    .tpl.add { --sk-bg:#F7F8FA; --sk-thumb:#EFF1F4; --sk-bar:#E8EBEF; --sk-line:#D5DBE1;
      background:var(--sk-bg); border:1.5px dashed var(--sk-line); box-shadow:none; }
    :host-context([data-theme='dark']) .tpl.add { --sk-bg:#1E2A22; --sk-thumb:#26332A; --sk-bar:#2F3D33; --sk-line:#3C4C41; }
    .tpl.add:hover { border-color:var(--mv-primary); box-shadow:none; }
    .tpl.add .thumb { background:var(--sk-thumb); }
    .tpl.add .mini { background:var(--sk-bg); box-shadow:none; }
    .tpl.add .mini i { background:var(--sk-bar); }
    .tpl.add .plus { align-self:center; margin-top:8px; width:30px; height:30px; border-radius:50%; background:var(--sk-thumb); color:var(--mv-text-muted); display:grid; place-items:center; font-size:1.25rem; line-height:1; transition:background .15s, color .15s; }
    .tpl.add:hover .plus { background:var(--mv-primary); color:#fff; }
    .tpl.add .bar { display:block; height:9px; border-radius:5px; background:var(--sk-bar); }
    .tpl.add .facts { border-top-color:var(--sk-bar); }
    .tpl.add b { font-size:.95rem; color:var(--mv-text-sub); }
    .tpl.add:hover b { color:var(--mv-primary); }

    /* Sjabloon */
    .dhead { display:flex; align-items:flex-start; gap:12px; flex-wrap:wrap; }
    .back { border:1px solid var(--mv-border); background:var(--mv-surface); border-radius:8px; width:34px; height:34px; color:var(--mv-text-sub); cursor:pointer; flex:none; margin-top:18px; }
    .back:hover { border-color:var(--mv-primary); color:var(--mv-primary); }
    .dtitle { flex:1; min-width:260px; display:flex; flex-direction:column; gap:2px; }
    .crumb { font-size:.72rem; color:var(--mv-text-muted); }
    .namerow { display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
    .namein { font:inherit; font-size:1.25rem; font-weight:700; letter-spacing:-.02em; color:var(--mv-text); border:1px solid transparent; border-radius:7px;
              background:transparent; padding:2px 6px; margin-left:-7px; min-width:12ch; }
    .namein:hover:not(:disabled) { border-color:var(--mv-border); }
    .namein:focus { outline:none; border-color:var(--mv-primary); background:var(--mv-surface); }
    .subin { font:inherit; font-size:.8rem; color:var(--mv-text-sub); border:1px solid transparent; border-radius:6px; background:transparent; padding:2px 6px; margin-left:-7px; min-width:30ch; }
    .subin:hover:not(:disabled) { border-color:var(--mv-border); }
    .subin:focus { outline:none; border-color:var(--mv-primary); background:var(--mv-surface); }
    .namein:disabled, .subin:disabled { color:inherit; }
    .linkbtn { border:none; background:none; padding:0; font:inherit; font-size:.78rem; font-weight:600; color:var(--mv-primary); cursor:pointer; }
    .dactions { display:flex; gap:8px; margin-top:18px; }
    .tabs { display:flex; gap:4px; border-bottom:1px solid var(--mv-border); margin:14px 0 16px; }
    .tab { border:none; background:none; padding:10px 14px; font:inherit; font-size:.875rem; font-weight:600; color:var(--mv-text-sub); cursor:pointer;
           border-bottom:2px solid transparent; margin-bottom:-1px; display:flex; align-items:center; gap:8px; }
    .tab.on { color:var(--mv-primary); border-bottom-color:var(--mv-primary); font-weight:700; }
    .tab small { font-size:.65rem; font-weight:700; padding:2px 7px; border-radius:999px; background:var(--mv-surface-alt); color:var(--mv-text-sub); }
    .tab.on small { background:var(--mv-primary-tint); color:var(--mv-primary); }
    .gvnote { font-size:.8rem; color:var(--mv-text-sub); background:var(--mv-surface-alt); border-radius:9px; padding:9px 12px; margin-bottom:12px; }

    /* Template teksten */
    .tlayout { display:grid; grid-template-columns:270px minmax(0,1fr); gap:16px; align-items:start; }
    @media (max-width:960px) { .tlayout { grid-template-columns:minmax(0,1fr); } }
    .tlist { padding:12px 8px; position:sticky; top:12px; max-height:calc(100vh - 40px); overflow:auto; }
    .search { width:100%; box-sizing:border-box; border:1px solid var(--mv-border); border-radius:8px; padding:7px 10px; font:inherit; font-size:.8rem; background:var(--mv-surface-alt); color:var(--mv-text); }
    .filters { display:flex; flex-wrap:wrap; gap:5px; margin:8px 2px 6px; }
    .filters button { border:1px solid var(--mv-border); background:var(--mv-surface); border-radius:999px; padding:3px 9px; font:inherit; font-size:.72rem; font-weight:600; color:var(--mv-text-sub); cursor:pointer; }
    .filters button.on { border-color:var(--mv-primary); background:var(--mv-primary-tint); color:var(--mv-primary); }
    .filters small { color:var(--mv-text-muted); margin-left:3px; }
    .chap { font-size:.66rem; font-weight:700; letter-spacing:.06em; text-transform:uppercase; color:var(--mv-text-muted); padding:12px 8px 4px; }
    .item { width:100%; border:none; background:none; border-radius:8px; padding:7px 8px; display:grid; grid-template-columns:42px minmax(0,1fr) 8px; gap:6px;
            align-items:center; text-align:left; cursor:pointer; font:inherit; }
    .item:hover { background:var(--mv-surface-alt); }
    .item.on { background:var(--mv-primary-tint); }
    .item .num { font-size:.72rem; color:var(--mv-text-muted); font-variant-numeric:tabular-nums; }
    .item b { display:block; font-size:.8125rem; font-weight:600; color:var(--mv-text); }
    .item.on b { color:var(--mv-primary); }
    .item small { font-size:.7rem; color:var(--mv-text-muted); }
    .item small.v { color:#7C4DDB; }
    .edot { width:7px; height:7px; border-radius:50%; background:#F6A609; }
    .editor { padding:18px 20px 20px; display:flex; flex-direction:column; gap:12px; }
    .ehead { display:flex; align-items:flex-start; gap:10px; }
    .ehead > div { flex:1; }
    .ehead .crumb { font-size:.72rem; }
    .ehead h3 { margin:2px 0 0; font-size:1.0625rem; font-weight:700; color:var(--mv-text); }
    .ehead p { margin:4px 0 0; font-size:.78rem; color:var(--mv-text-sub); line-height:1.5; }
    .vcard { border:1px solid var(--mv-border); border-radius:11px; padding:12px 14px; display:flex; flex-direction:column; gap:6px; cursor:pointer; background:var(--mv-surface); }
    .vcard:hover { border-color:var(--mv-text-muted); }
    .vcard.on { border-color:var(--mv-primary); cursor:default; box-shadow:0 0 0 1px var(--mv-primary); }
    .when { font-size:.75rem; color:var(--mv-text-sub); }
    .when b { font-weight:500; color:#5B2BC4; }
    :host-context([data-theme='dark']) .when b { color:#C4AEF5; }
    .vname { display:flex; align-items:center; gap:8px; font-size:.84rem; font-weight:700; color:var(--mv-text); }
    .vname .tag { font-size:.62rem; }
    .vtext { font-size:.8125rem; color:var(--mv-text-sub); line-height:1.6; white-space:pre-line; }
    textarea.body { width:100%; box-sizing:border-box; min-height:110px; border:1px solid var(--mv-border); border-radius:8px; padding:10px 12px; font:inherit;
                    font-size:.84rem; line-height:1.6; color:var(--mv-text); background:var(--mv-surface); resize:vertical; }
    textarea.body:focus { outline:none; border-color:var(--mv-primary); }
    .insert { display:flex; flex-wrap:wrap; gap:5px; align-items:center; font-size:.72rem; color:var(--mv-text-muted); }
    .insert button { border:1px solid var(--mv-border); background:var(--mv-surface-alt); border-radius:6px; padding:2px 7px; font-family:ui-monospace, Menlo, monospace;
                     font-size:.7rem; font-weight:600; color:#2F6B22; cursor:pointer; }
    .insert button.op { font-family:inherit; color:var(--mv-text-sub); font-weight:500; }
    .vact { display:flex; gap:8px; align-items:center; flex-wrap:wrap; margin-top:2px; }
    details.rule { margin-top:4px; }
    details.rule summary { font-size:.7rem; color:var(--mv-text-muted); cursor:pointer; list-style:none; }
    details.rule summary::-webkit-details-marker { display:none; }
    details.rule summary::before { content:'▸ '; }
    details.rule[open] summary::before { content:'▾ '; }
    details.rule p { font-size:.7rem; color:var(--mv-text-muted); margin:6px 0 4px; }
    details.rule textarea { width:100%; box-sizing:border-box; min-height:70px; border:1px dashed var(--mv-border); border-radius:7px; padding:6px 8px;
                            font-family:ui-monospace, Menlo, monospace; font-size:.68rem; color:var(--mv-text-sub); background:var(--mv-surface-alt); }
    .viewseg { display:flex; align-items:center; gap:4px; }
    .viewseg button { border:1px solid var(--mv-border); background:var(--mv-surface); border-radius:7px; padding:3px 10px; font:inherit; font-size:.72rem;
                      font-weight:600; color:var(--mv-text-sub); cursor:pointer; }
    .viewseg button.on { border-color:var(--mv-primary); background:var(--mv-primary-tint); color:var(--mv-primary); }
    .viewseg span { font-size:.7rem; color:var(--mv-text-muted); margin-left:6px; }
    .astext { border:1px solid var(--mv-border-soft); border-radius:8px; padding:10px 12px; background:var(--mv-surface-alt); }
    .astext p { margin:0 0 6px; font-size:.84rem; line-height:1.65; color:var(--mv-text); }
    .astext p:last-child { margin-bottom:0; }
    .astext p.bullet { padding-left:14px; text-indent:-10px; }
    .ex { background:var(--mv-primary-tint); color:#2F6B22; border-radius:4px; padding:1px 4px; font-weight:600; }
    .ex.bad { background:var(--mv-danger-bg); color:var(--mv-danger); }

    /* Document opmaak */
    .llayout { display:grid; grid-template-columns:minmax(0,1.5fr) minmax(280px,1fr); gap:16px; align-items:start; }
    @media (max-width:1100px) { .llayout { grid-template-columns:minmax(0,1fr); } }
    .pv { padding:14px 16px 10px; }
    .pvhead { display:flex; align-items:center; gap:8px; flex-wrap:wrap; margin-bottom:10px; }
    .pvhead b { flex:1; font-size:.875rem; color:var(--mv-text); }
    .seg { border:1px solid var(--mv-border); background:var(--mv-surface); border-radius:7px; padding:4px 9px; font:inherit; font-size:.72rem; font-weight:600; color:var(--mv-text-sub); cursor:pointer; }
    .seg.on { border-color:var(--mv-primary); background:var(--mv-primary-tint); color:var(--mv-primary); }
    .seg .dotc { display:inline-block; width:7px; height:7px; border-radius:2px; background:var(--mv-primary); margin-right:5px; }
    .stage { background:#E9ECEF; border-radius:10px; height:620px; overflow:auto; position:relative; }
    :host-context([data-theme='dark']) .stage { background:#0F1511; }
    .docx-holder { zoom:.62; padding:20px 0; }
    .docx-holder .docx-wrapper { background:transparent !important; padding:0 !important; }
    .docx-holder section.docx { margin:0 auto 24px !important; box-shadow:0 6px 18px rgba(24,28,50,.12) !important; isolation:isolate; }
    /* docx-preview zet een standaardkleur op elke span in het document; onze veld-span neemt die
       van zijn run over, anders wordt witte tekst op een donker vlak onzichtbaar. */
    .docx-holder .mf { border-radius:3px; color:inherit !important; font-size:inherit !important; font-weight:inherit !important; }
    .stage.fields .mf { background:#E9F5E6; color:#2F6B22 !important; outline:1px solid #b8dcae; }
    .pvfoot { text-align:center; font-size:.72rem; color:var(--mv-text-muted); margin-top:8px; }
    .stagemsg { position:absolute; inset:0; display:flex; align-items:center; justify-content:center; font-size:.84rem; color:var(--mv-text-sub); padding:20px; text-align:center; }
    .side { display:flex; flex-direction:column; gap:14px; }
    .filecard { padding:14px 16px; display:flex; flex-direction:column; gap:10px; }
    .filehead { display:flex; gap:12px; align-items:flex-start; }
    .docico { width:30px; height:36px; flex:none; border-radius:4px; background:#E8EEFD; color:#1D4ED8; font-size:.55rem; font-weight:700; display:flex; align-items:flex-end;
              justify-content:center; padding-bottom:4px; }
    .filehead b { display:block; font-size:.875rem; color:var(--mv-text); }
    .filehead small { font-size:.75rem; color:var(--mv-text-sub); line-height:1.45; }
    .fileact { display:flex; gap:8px; flex-wrap:wrap; align-items:center; }
    .quiet { border:none; background:none; font:inherit; font-size:.78rem; color:var(--mv-text-muted); cursor:pointer; }
    .quiet:hover { color:var(--mv-danger); }
    .checks { padding:14px 16px; display:flex; flex-direction:column; gap:10px; }
    .checks .ch { display:flex; justify-content:space-between; align-items:baseline; }
    .checks .ch b { font-size:.875rem; color:var(--mv-text); }
    .checks .ch span { font-size:.72rem; font-weight:600; }
    .flist { border:1px solid var(--mv-border); border-radius:9px; overflow:hidden; }
    .frow { display:flex; align-items:center; gap:8px; padding:6px 10px; border-top:1px solid var(--mv-border-soft); font-size:.75rem; }
    .frow:first-child { border-top:none; }
    .frow.miss { background:#FFF8EC; }
    :host-context([data-theme='dark']) .frow.miss { background:#33280F; }
    .frow code { font-family:ui-monospace, Menlo, monospace; font-size:.72rem; font-weight:600; color:var(--mv-text); flex:1; overflow:hidden; text-overflow:ellipsis; }
    .frow span:last-child { color:var(--mv-text-muted); white-space:nowrap; }
    .frow.miss span:last-child { color:var(--mv-warning-text); }
    .okdot, .warndot { width:16px; height:16px; flex:none; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:.6rem; font-weight:800; color:#fff; }
    .okdot { background:var(--mv-primary); } .warndot { background:#F6A609; }
    .cnote { font-size:.72rem; color:var(--mv-text-muted); }
    .cline { display:flex; gap:8px; align-items:flex-start; padding-top:8px; border-top:1px solid var(--mv-border-soft); font-size:.78rem; color:var(--mv-text); }
    .cline small { display:block; color:var(--mv-text-muted); font-size:.7rem; }

    /* Popup */
    .overlay { position:fixed; inset:0; z-index:var(--mv-z-modal, 1200); background:rgba(24,28,50,.45); backdrop-filter:blur(3px); display:flex; align-items:center; justify-content:center; padding:24px; }
    .modal { width:100%; max-width:640px; max-height:calc(100vh - 48px); overflow:auto; background:var(--mv-surface); border-radius:18px; box-shadow:0 24px 60px rgba(24,28,50,.18); padding:20px 22px; display:flex; flex-direction:column; gap:14px; }
    .mhead { display:flex; gap:12px; align-items:flex-start; }
    .mhead div { flex:1; }
    .mhead h3 { margin:0; font-size:1.125rem; font-weight:700; color:var(--mv-text); }
    .mhead p { margin:4px 0 0; font-size:.8rem; color:var(--mv-text-sub); line-height:1.5; }
    .x { width:32px; height:32px; border:none; background:var(--mv-surface-alt); border-radius:8px; color:var(--mv-text-sub); cursor:pointer; }
    .steps { display:grid; grid-template-columns:repeat(3, 1fr); gap:8px; }
    @media (max-width:560px) { .steps { grid-template-columns:1fr; } }
    .step { background:var(--mv-surface-alt); border-radius:10px; padding:10px; font-size:.72rem; color:var(--mv-text-sub); line-height:1.45; }
    .step i { display:inline-flex; width:18px; height:18px; border-radius:50%; border:1.5px solid var(--mv-primary); color:var(--mv-primary); align-items:center; justify-content:center; font-style:normal; font-size:.65rem; font-weight:700; margin-bottom:5px; }
    .step b { display:block; font-size:.78rem; color:var(--mv-text); margin-bottom:2px; }
    .drop { border:1.5px dashed var(--mv-border); border-radius:12px; padding:22px; text-align:center; cursor:pointer; display:flex; flex-direction:column; align-items:center; gap:6px; }
    .drop.over { border-color:var(--mv-primary); background:var(--mv-primary-light); }
    .drop .arrow { width:28px; height:28px; border-radius:50%; background:var(--mv-primary-tint); color:var(--mv-primary); display:flex; align-items:center; justify-content:center; font-weight:700; }
    .drop b { font-size:.84rem; color:var(--mv-text); }
    .drop span { font-size:.75rem; color:var(--mv-text-sub); }
    .drop span em { color:var(--mv-primary); font-style:normal; font-weight:600; }
    .mcols { display:grid; grid-template-columns:1.3fr 1fr; gap:14px; }
    @media (max-width:560px) { .mcols { grid-template-columns:1fr; } }
    .mcols h4 { margin:0 0 3px; font-size:.8rem; color:var(--mv-text); }
    .mcols p { margin:0 0 8px; font-size:.72rem; color:var(--mv-text-sub); line-height:1.45; }
    .mfrow { display:grid; grid-template-columns:minmax(0,1.3fr) minmax(0,1fr) auto; gap:6px; align-items:center; padding:5px 8px; border-top:1px solid var(--mv-border-soft);
             font-size:.72rem; cursor:pointer; }
    .mfrow:first-child { border-top:none; }
    .mfrow:hover { background:var(--mv-surface-alt); }
    .mfrow code { font-family:ui-monospace, Menlo, monospace; font-weight:600; color:var(--mv-text); overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
    .mfrow span { color:var(--mv-text-sub); overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
    .mfrow em { font-style:normal; color:var(--mv-text-muted); font-size:.68rem; }
    .mfrow em.ok { color:var(--mv-primary); }
    .example { background:var(--mv-surface-alt); border-radius:9px; padding:10px 12px; font-family:ui-monospace, Menlo, monospace; font-size:.72rem; color:var(--mv-text); line-height:1.7; }
    .example small { display:block; font-family:inherit; color:var(--mv-text-muted); font-size:.68rem; }
    .example .code { background:var(--mv-primary-tint); color:#2F6B22; border-radius:4px; padding:1px 4px; }
  `];

  constructor() {
    super();
    this.canEdit = false;
    this._templates = [];
    this._known = [];
    this._loading = true;
    this._error = '';
    this._busy = false;
    this._tplId = null;
    this._tab = 'texts';
    this._texts = [];
    this._textsCanEdit = false;
    this._section = null;
    this._key = null;
    this._draft = null;
    this._filter = 'all';
    this._query = '';
    this._uploadOpen = false;
    this._dragOver = false;
    this._showFields = false;
    this._page = 'cover';
    this._previewState = null;
    this._copied = null;
    this._asText = false;
    this._renderedFor = null;
  }

  connectedCallback() { super.connectedCallback(); this._load(); }

  get _tpl() { return this._templates.find((t) => t.id === this._tplId) || null; }

  async _load() {
    this._loading = true;
    try {
      const res = await api.getReportTemplates();
      this._templates = res?.templates || [];
      this._known = res?.knownMergeFields || [];
    } catch (e) {
      this._error = e.message || 'Sjablonen laden mislukt.';
    } finally {
      this._loading = false;
    }
  }

  async _open(id, tab = 'texts') {
    this._tplId = id;
    this._tab = tab;
    this._error = '';
    this._section = null;
    this._key = null;
    this._draft = null;
    this._renderedFor = null;
    await this._loadTexts();
  }

  async _loadTexts() {
    const id = this._tplId;
    try {
      const res = await api.getTemplateTexts(id);
      if (id !== this._tplId) return;
      this._texts = res?.texts || [];
      this._textsCanEdit = !!res?.canEdit;
      if (!this._section) this._pickSection(this._sections()[0]?.key);
    } catch (e) {
      this._error = e.message || 'Teksten laden mislukt.';
    }
  }

  // ── Afgeleid ─────────────────────────────────────────────────────────────

  /** De paragrafen met teksten, op volgorde: { key, title, chapter, texts, variant, edited }. */
  _sections() {
    const by = new Map();
    for (const t of this._texts) {
      if (!by.has(t.section)) by.set(t.section, []);
      by.get(t.section).push(t);
    }
    return [...by.keys()].sort(sortSections).map((key) => {
      const texts = by.get(key);
      const variant = texts.some((t) => t.kind === 'variant');
      return {
        key, texts, variant, chapter: sectionChapter(key),
        title: SECTION_TITLES.get(key) || texts[0].label,
        edited: texts.some((t) => t.custom),
      };
    });
  }

  _pickSection(key, textKey = null) {
    this._section = key || null;
    const s = this._sections().find((x) => x.key === key);
    const t = s?.texts.find((x) => x.key === textKey) || s?.texts[0] || null;
    this._pickText(t);
  }

  _pickText(t) {
    this._asText = false;
    this._key = t?.key || null;
    this._draft = t ? { body: t.body, rule: t.rule ? JSON.stringify(t.rule, null, 2) : '' } : null;
  }

  get _currentText() { return this._texts.find((t) => t.key === this._key) || null; }

  get _draftChanged() {
    const t = this._currentText;
    if (!t || !this._draft) return false;
    return this._draft.body !== t.body || (t.kind === 'variant' && this._draft.rule.trim() !== (t.rule ? JSON.stringify(t.rule, null, 2) : '').trim());
  }

  // ── Acties ───────────────────────────────────────────────────────────────

  async _act(fn) {
    this._busy = true;
    this._error = '';
    try { return await fn(); } catch (e) { this._error = e.message || 'Er ging iets mis.'; return null; } finally { this._busy = false; }
  }

  async _newTemplate() {
    const t = await this._act(() => api.createReportTemplate({ name: 'Nieuw sjabloon', subtitle: 'NEN 5740 · verkennend' }));
    if (!t) return;
    await this._load();
    await this._open(t.id, 'layout');
  }

  async _duplicate() {
    const src = this._tpl;
    const t = await this._act(() => api.createReportTemplate({ copyFrom: src.id }));
    if (!t) return;
    await this._load();
    await this._open(t.id, this._tab);
  }

  async _remove() {
    const t = this._tpl;
    const ok = await confirmDialog({
      title: 'Sjabloon verwijderen',
      message: `"${t.name}" verwijderen, met de eigen teksten en het Word-bestand? Rapporten die het gebruikten, krijgen het standaardsjabloon.`,
      confirmLabel: 'Verwijderen', danger: true,
    });
    if (!ok) return;
    if (await this._act(() => api.deleteReportTemplate(t.id))) {
      this._tplId = null;
      await this._load();
    }
  }

  async _update(data) {
    const res = await this._act(() => api.updateReportTemplate(this._tplId, data));
    if (res?.templates) this._templates = res.templates;
  }

  async _saveText() {
    const t = this._currentText;
    let rule;
    if (t.kind === 'variant') {
      try { rule = this._draft.rule.trim() ? JSON.parse(this._draft.rule) : null; } catch (_) { this._error = 'De technische regel is geen geldige JSON.'; return; }
    }
    const res = await this._act(() => api.saveTemplateText(this._tplId, { key: t.key, body: this._draft.body, ...(t.kind === 'variant' ? { rule } : {}) }));
    if (!res) return;
    this._texts = res.texts || this._texts;
    this._pickText(this._currentText);
    this._refreshCounts();
  }

  async _resetText() {
    const t = this._currentText;
    const res = await this._act(() => api.resetTemplateText(this._tplId, t.key));
    if (!res) return;
    this._texts = res.texts || this._texts;
    this._pickText(this._currentText);
    this._refreshCounts();
  }

  _refreshCounts() {
    const edited = this._texts.filter((t) => t.custom).length;
    this._templates = this._templates.map((t) => (t.id === this._tplId ? { ...t, textsEdited: edited } : t));
  }

  async _uploadFile(file) {
    if (!file) return;
    if (!/\.(docx|dotx)$/i.test(file.name)) { this._error = 'Kies een Word-bestand (.docx of .dotx).'; return; }
    const t = await this._act(() => api.uploadReportTemplateFile(this._tplId, file));
    if (!t) return;
    this._uploadOpen = false;
    this._renderedFor = null;
    await this._load();
  }

  async _removeFile() {
    const ok = await confirmDialog({
      title: 'Terug naar het standaardformat',
      message: 'Het eigen Word-bestand van dit sjabloon verwijderen? Rapporten krijgen dan weer het standaardformat; de teksten blijven.',
      confirmLabel: 'Terugzetten',
    });
    if (!ok) return;
    if (await this._act(() => api.deleteReportTemplateFile(this._tplId))) {
      this._renderedFor = null;
      await this._load();
    }
  }

  async _download(id = this._tplId) {
    const r = await this._act(() => api.downloadReportTemplateFile(id));
    if (!r) return;
    const url = URL.createObjectURL(r.blob);
    const a = Object.assign(document.createElement('a'), { href: url, download: r.filename });
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  }

  _copy(name) {
    try { navigator.clipboard?.writeText(`«${name}»`); } catch (_) { /* geen klembord */ }
    this._copied = name;
    clearTimeout(this._copyTimer);
    this._copyTimer = setTimeout(() => { this._copied = null; }, 1400);
  }

  _insert(snippet) {
    const ta = this.renderRoot.querySelector('textarea.body');
    const body = this._draft.body;
    const s = ta ? ta.selectionStart : body.length, e = ta ? ta.selectionEnd : body.length;
    this._draft = { ...this._draft, body: body.slice(0, s) + snippet + body.slice(e) };
    this.updateComplete.then(() => { if (ta) { ta.focus(); ta.setSelectionRange(s + snippet.length, s + snippet.length); } });
  }

  // ── Voorbeeld van het Word-bestand ──────────────────────────────────────

  updated() {
    if (this._tplId && this._tab === 'layout') this._renderPreview();
  }

  /**
   * Het voorbeeld in de browser (docx-preview): een voorbeeldrapport dat de backend op dit
   * sjabloon maakt, of met 'Samenvoegvelden tonen' het Word-bestand zelf met de veldnamen.
   */
  async _renderPreview() {
    const t = this._tpl;
    if (!t) return;
    // Zonder 'Samenvoegvelden tonen' een voorbeeldrapport (verzonnen project, de teksten van dit
    // sjabloon); met, het Word-bestand zelf met de veldnamen.
    const raw = this._showFields;
    const stamp = `${t.id}:${t.uploadedAt || ''}:${raw ? 'raw' : 'sample'}`;
    if (this._renderedFor === stamp) return;
    this._renderedFor = stamp;
    const holder = this.renderRoot.querySelector('.docx-holder');
    const styles = this.renderRoot.querySelector('.docx-styles');
    if (!holder || !styles) { this._renderedFor = null; return; }
    this._previewState = 'loading';
    try {
      const [{ renderAsync }, file] = await Promise.all([import('docx-preview'),
        raw ? api.downloadReportTemplateFile(t.id) : api.previewReportTemplate(t.id)]);
      if (this._renderedFor !== stamp) return;
      holder.innerHTML = '';
      styles.innerHTML = '';
      await renderAsync(file.blob, holder, styles, {
        className: 'docx', inWrapper: true, breakPages: true, ignoreLastRenderedPageBreak: false,
        renderHeaders: true, renderFooters: true, renderFootnotes: false, experimental: true, useBase64URL: true,
      });
      this._fixLayout(holder, styles);
      this._markFields(holder);
      this._applyFields();
      this._wrapTextBoxes(holder);
      this._previewState = 'ok';
    } catch (e) {
      console.error('docx-preview:', e);
      this._previewState = 'error';
    }
  }

  /**
   * Twee dingen die docx-preview anders doet dan Word, en die een voorblad als dat van MV
   * onleesbaar maken:
   *   - een zwevende afbeelding achter de tekst (paginagrote achtergrond) telt mee in de hoogte
   *     van zijn alinea, waardoor alles eronder een pagina zakt. In Word neemt hij geen ruimte in.
   *   - een tekstvak (VML) krijgt width="0" height="0": de tekst erin is er wel, maar onzichtbaar.
   */
  _fixLayout(root, styles) {
    officeFontFallbacks();
    // Opsommingstekens uit Symbol en Wingdings (privétekens) worden in de browser een vierkantje.
    for (const st of styles?.querySelectorAll('style') || []) {
      const fixed = st.textContent
        .replace(/\uF0B7/g, '•').replace(/\uF0A7/g, '▪').replace(/\uF0D8/g, '➢').replace(/\uF0FC/g, '✓').replace(/\uF076/g, '❖')
        .replace(/font-family:\s*(?:Symbol|Wingdings)\s*;/gi, 'font-family: inherit;');
      if (fixed !== st.textContent) st.textContent = fixed;
    }
    for (const wrap of root.querySelectorAll('span[style], div[style]')) {
      if (wrap.style.width !== '0px' || wrap.style.height !== '0px') continue;
      for (const child of wrap.children) {
        if (!/^(img|svg)$/i.test(child.tagName)) continue;
        // Achter de tekst, maar boven het wit van de pagina (die is een eigen stapel, zie CSS).
        child.style.position = 'absolute';
        child.style.zIndex = '-1';
        // Een paginagrote achtergrond hoort tegen de paginarand, niet tegen de tekstkolom.
        const sec = wrap.closest('section.docx');
        if (sec && parseFloat(child.style.width) * 4 / 3 >= sec.clientWidth * .9) {
          child.style.left = `${-(wrap.getBoundingClientRect().left - sec.getBoundingClientRect().left) / (sec.getBoundingClientRect().width / sec.offsetWidth)}px`;
        }
      }
    }
    // Een lege alinea is in Word één regel hoog, in docx-preview bijna niets. Een voorblad dat
    // met lege alinea's is opgemaakt, schuift dan omhoog.
    for (const p of root.querySelectorAll('section.docx p')) {
      if (p.textContent || p.querySelector('img, svg, br')) continue;
      (p.lastElementChild?.tagName === 'SPAN' ? p.lastElementChild : p).append('\u200b');
    }
    for (const svg of root.querySelectorAll('svg[width="0"][height="0"]')) {
      // Tekst verticaal gecentreerd in het vak, zoals Word (v-text-anchor:middle). Eerst lezen:
      // na een wijziging van svg.style is die (voor de browser onbekende) eigenschap weg.
      const middle = /v-text-anchor:\s*middle/.test(svg.getAttribute('style') || '');
      svg.removeAttribute('width');
      svg.removeAttribute('height');
      svg.style.overflow = 'visible';
      const fo = svg.querySelector('foreignObject');
      if (fo && middle) {
        const box = document.createElement('div');
        box.style.cssText = 'height:100%;display:flex;flex-direction:column;justify-content:center';
        box.append(...fo.childNodes);
        fo.append(box);
      }
    }
  }

  /**
   * Tekst loopt in Word om een breed tekstvak heen (terugloop 'vierkant'), docx-preview legt het
   * vak er los overheen. Bij een vak dat het grootste deel van de breedte beslaat komt de tekst in
   * Word eronder; dat doen we na: de eerste alinea zonder eigen vak schuift tot onder de vakken.
   * Pas na het tekenen, want het werkt met de posities op het scherm.
   */
  _wrapTextBoxes(root) {
    for (const sec of root.querySelectorAll('section.docx')) {
      const scale = sec.getBoundingClientRect().width / (sec.offsetWidth || 1);
      const contentW = (sec.clientWidth - parseFloat(getComputedStyle(sec).paddingLeft) - parseFloat(getComputedStyle(sec).paddingRight)) * scale;
      let below = null;
      for (const p of sec.querySelectorAll(':scope > article > p, :scope > p')) {
        const boxes = [...p.querySelectorAll('svg')].filter((svg) => svg.style.position === 'absolute' && svg.getBoundingClientRect().width > contentW * .5);
        if (boxes.length) {
          below = Math.max(below ?? 0, ...boxes.map((b) => b.getBoundingClientRect().bottom));
          continue;
        }
        if (below == null) continue;
        const top = p.getBoundingClientRect().top;
        if (top < below) p.style.marginTop = `${(parseFloat(getComputedStyle(p).marginTop) || 0) + (below - top) / scale}px`;
        below = null;
      }
    }
  }

  /** Elk «veld» en [plaatshouder] in een eigen span, met de veldnaam en de voorbeeldwaarde. */
  _markFields(root) {
    // Word verdeelt een «veld» vaak over meerdere runs (spellingcontrole, opmaak): dan staat het
    // in meerdere tekstknopen. Eerst samenvoegen in de eerste knoop, per alinea.
    for (const p of root.querySelectorAll('p')) {
      const text = p.textContent;
      if (!text.includes('«')) continue;
      const tw = document.createTreeWalker(p, window.NodeFilter.SHOW_TEXT);
      const nodes = [];
      while (tw.nextNode()) nodes.push(tw.currentNode);
      let open = null;
      for (const node of nodes) {
        let v = node.nodeValue;
        if (open) {
          const end = v.indexOf('»');
          open.nodeValue += end < 0 ? v : v.slice(0, end + 1);
          v = end < 0 ? '' : v.slice(end + 1);
          node.nodeValue = v;
          if (end < 0) continue;
          open = null;
        }
        const last = v.lastIndexOf('«');
        if (last >= 0 && v.indexOf('»', last) < 0) open = node;
      }
    }
    const walker = document.createTreeWalker(root, window.NodeFilter.SHOW_TEXT);
    const nodes = [];
    // Niet FIELD_RE.test: die regex is globaal en onthoudt waar hij was, dan sla je velden over.
    while (walker.nextNode()) if (/«[^«»]{1,60}»|\[(?:adres|percelen|titel|onderwerp)\]/.test(walker.currentNode.nodeValue)) nodes.push(walker.currentNode);
    for (const node of nodes) {
      const text = node.nodeValue;
      const frag = document.createDocumentFragment();
      let last = 0;
      for (const m of text.matchAll(FIELD_RE)) {
        if (m.index > last) frag.append(text.slice(last, m.index));
        const name = m[1] ?? m[2];
        const span = document.createElement('span');
        span.className = 'mf';
        span.dataset.field = m[0];
        span.dataset.example = MERGE_EXAMPLES[name] ?? m[0];
        frag.append(span);
        last = m.index + m[0].length;
      }
      if (last < text.length) frag.append(text.slice(last));
      node.replaceWith(frag);
    }
  }

  _applyFields() {
    for (const s of this.renderRoot.querySelectorAll('.docx-holder .mf')) s.textContent = this._showFields ? s.dataset.field : s.dataset.example;
  }

  _goPage(page) {
    this._page = page;
    const pages = [...this.renderRoot.querySelectorAll('.docx-holder section.docx')];
    if (!pages.length) return;
    // Naar de tekst zelf, niet naar de bovenkant van de pagina: een pagina kan beginnen met een
    // paginagrote achtergrond uit de koptekst, en dan zie je alleen wit.
    const paras = [...this.renderRoot.querySelectorAll('.docx-holder section.docx p')];
    const tocPara = paras.find((p) => /^\s*inhoud(sopgave)?\s*$/i.test(p.textContent));
    let target = pages[0];
    if (page === 'toc') target = tocPara || pages[1] || pages[0];
    if (page === 'body') {
      const start = tocPara ? paras.indexOf(tocPara) : 0;
      // De eerste gewone alinea na de inhoudsopgave die geen inhoudsopgaveregel is.
      target = paras.slice(start + 1).find((p) => p.textContent.trim().length > 120 && !p.querySelector('a[href^="#"]') && !p.closest('header, footer'))
        || pages[pages.length - 1];
    }
    const stage = this.renderRoot.querySelector('.stage');
    if (!stage) return;
    // Met de schermposities rekenen: de pagina's zijn verkleind (zoom), offsetTop klopt dan niet.
    const top = target.getBoundingClientRect().top - stage.getBoundingClientRect().top + stage.scrollTop - (page === 'cover' ? 12 : 40);
    stage.scrollTo({ top, behavior: 'smooth' });
  }

  // ── Weergave ─────────────────────────────────────────────────────────────

  render() {
    if (this._loading && !this._templates.length) return html`<p class="muted">Laden…</p>`;
    return html`
      ${this._tplId && this._tpl ? this._renderDetail() : this._renderOverview()}
      ${this._uploadOpen ? this._renderUpload() : ''}`;
  }

  _renderOverview() {
    return html`
      <div class="ohead">
        <div>
          <h2>Rapportagesjablonen <span class="beta">BÈTA</span></h2>
          <p>Elk sjabloon heeft eigen teksten en een eigen document opmaak. Bij een nieuw rapport kies je welk sjabloon je gebruikt.</p>
        </div>
      </div>
      ${this._error ? html`<p class="err">${this._error}</p>` : ''}
      <div class="grid">
        ${this._templates.map((t) => html`
          <button class="card tpl" @click=${() => this._open(t.id)}>
            <div class="thumb ${t.hasFile ? 'blue' : 'green'}"><div class="mini"><i class="logo"></i><i class="t"></i><i></i><i style="width:70%"></i></div></div>
            <div class="tbody">
              <div class="tname"><b>${t.name}</b>
                ${t.isDefault ? html`<span class="badge">Standaard</span>` : t.builtIn ? html`<span class="badge gv">GeoVoice</span>` : ''}</div>
              ${t.subtitle ? html`<span class="tsub">${t.subtitle}</span>` : ''}
              <div class="facts">
                <span>Opmaak</span><span>${t.hasFile ? t.fileName : 'Standaardformat'}</span>
                <span>Teksten</span><span>${t.textsEdited ? `${t.textsEdited} aangepast` : 'Standaardteksten'}</span>
                <span>Gebruikt in</span><span>${n(t.usedIn || 0, 'rapport', 'rapporten')}</span>
              </div>
            </div>
          </button>`)}
        ${this.canEdit ? html`
          <button class="card tpl add" ?disabled=${this._busy} @click=${() => this._newTemplate()}>
            <div class="thumb"><div class="mini"><i style="width:26px;height:7px"></i><i style="width:80%"></i><span class="plus">+</span></div></div>
            <div class="tbody">
              <b>Extra sjabloon aanmaken</b>
              <span class="tsub">Met eigen teksten en een eigen Word-opmaak</span>
              <div class="facts" style="grid-template-columns:1fr; gap:8px">
                <span class="bar" style="width:70%"></span><span class="bar" style="width:55%"></span><span class="bar" style="width:62%"></span>
              </div>
            </div>
          </button>` : ''}
      </div>`;
  }

  _renderDetail() {
    const t = this._tpl;
    const edit = this.canEdit && !t.builtIn;
    const sections = this._sections();
    return html`
      <div class="dhead">
        <button class="back" title="Alle sjablonen" @click=${() => { this._tplId = null; this._load(); }}>←</button>
        <div class="dtitle">
          <span class="crumb">Rapportagesjablonen</span>
          <div class="namerow">
            <input class="namein" .value=${t.name} ?disabled=${!edit} maxlength="120" size=${Math.max(12, t.name.length + 1)}
                   @change=${(e) => { const v = e.target.value.trim(); if (v && v !== t.name) this._update({ name: v }); else e.target.value = t.name; }}>
            ${t.isDefault ? html`<span class="badge">Standaard</span>`
              : this.canEdit ? html`<button class="linkbtn" ?disabled=${this._busy} @click=${() => this._update({ isDefault: true })}>Maak standaard</button>` : ''}
          </div>
          ${edit ? html`<input class="subin" .value=${t.subtitle || ''} placeholder="Omschrijving, bijv. NEN 5740 · verkennend" maxlength="160"
                   @change=${(e) => this._update({ subtitle: e.target.value })}>`
            : html`<span class="tsub">${t.subtitle || ''}</span>`}
          <span class="crumb">gebruikt in ${n(t.usedIn || 0, 'rapport', 'rapporten')}</span>
        </div>
        <div class="dactions">
          ${this.canEdit ? html`<button class="btn" ?disabled=${this._busy} @click=${() => this._duplicate()}>Dupliceren</button>` : ''}
          ${edit ? html`<button class="btn ghost" ?disabled=${this._busy} @click=${() => this._remove()}>Verwijderen</button>` : ''}
        </div>
      </div>
      <nav class="tabs">
        <button class="tab ${this._tab === 'texts' ? 'on' : ''}" @click=${() => { this._tab = 'texts'; }}>Template teksten <small>${n(sections.length, 'paragraaf', 'paragrafen')}</small></button>
        <button class="tab ${this._tab === 'layout' ? 'on' : ''}" @click=${() => { this._tab = 'layout'; this._renderedFor = null; }}>Document opmaak <small>${t.hasFile ? 'Eigen' : 'Standaard'}</small></button>
      </nav>
      ${this._error ? html`<p class="err">${this._error}</p>` : ''}
      ${t.builtIn ? html`<div class="gvnote">Dit is het sjabloon van GeoVoice. Je kunt het als standaard gebruiken, maar niet aanpassen.
        ${this.canEdit ? html`Klik op <b>Dupliceren</b> om eigen teksten of een eigen Word-bestand te gebruiken.` : ''}</div>` : ''}
      ${this._tab === 'texts' ? this._renderTexts(sections) : this._renderLayout()}`;
  }

  // ── Template teksten ─────────────────────────────────────────────────────

  _renderTexts(sections) {
    const q = this._query.trim().toLowerCase();
    const counts = {
      all: sections.length, fixed: sections.filter((s) => !s.variant).length,
      variant: sections.filter((s) => s.variant).length, edited: sections.filter((s) => s.edited).length,
    };
    const visible = sections.filter((s) => (this._filter === 'all' || (this._filter === 'edited' ? s.edited : this._filter === 'variant' ? s.variant : !s.variant))
      && (!q || `${s.key} ${s.title} ${s.texts.map((t) => `${t.label} ${t.body}`).join(' ')}`.toLowerCase().includes(q)));
    const chapters = [];
    for (const s of visible) {
      let c = chapters.find((x) => x.title === s.chapter);
      if (!c) chapters.push(c = { title: s.chapter, items: [] });
      c.items.push(s);
    }
    return html`
      <div class="tlayout">
        <div class="card tlist">
          <input class="search" placeholder="Zoek tekst of paragraaf" .value=${this._query} @input=${(e) => { this._query = e.target.value; }}>
          <div class="filters">
            ${[['all', 'Alle'], ['fixed', 'Vaste tekst'], ['variant', 'Met versies'], ['edited', 'Aangepast']].map(([k, l]) => html`
              <button class=${this._filter === k ? 'on' : ''} @click=${() => { this._filter = k; }}>${l}<small>${counts[k]}</small></button>`)}
          </div>
          ${chapters.map((c) => html`
            <div class="chap">${c.title}</div>
            ${c.items.map((s) => html`
              <button class="item ${s.key === this._section ? 'on' : ''}" @click=${() => this._pickSection(s.key)}>
                <span class="num">${s.key}</span>
                <span><b>${s.title}</b>${s.variant
                  ? html`<small class="v">${n(s.texts.length, 'versie', 'versies')}, kiest zelf</small>`
                  : html`<small>${s.texts.length > 1 ? n(s.texts.length, 'vaste tekst', 'vaste teksten') : 'Vaste tekst'}</small>`}</span>
                ${s.edited ? html`<span class="edot" title="Aangepast"></span>` : html`<span></span>`}
              </button>`)}`)}
          ${chapters.length ? '' : html`<p class="muted" style="padding:8px">Niets gevonden.</p>`}
        </div>
        <div style="display:flex; flex-direction:column; gap:14px; min-width:0;">
          ${this._renderSectionEditor(sections.find((s) => s.key === this._section))}
        </div>
      </div>`;
  }

  _renderSectionEditor(s) {
    if (!s) return html`<div class="card editor"><p class="muted">Kies links een paragraaf.</p></div>`;
    const canEdit = this._textsCanEdit;
    return html`
      <div class="card editor">
        <div class="ehead">
          <div>
            <span class="crumb">§ ${s.key} · ${s.chapter.replace(/^\d+\s/, '')}</span>
            <h3>${s.title}</h3>
            <p>${s.variant
              ? `Van deze paragraaf bestaan ${s.texts.length} versies. GeoVoice zet in elk rapport automatisch de versie die past bij de resultaten. Klik op een versie om ${canEdit ? 'de tekst aan te passen' : 'hem te bekijken'}.`
              : 'Deze tekst komt in elk rapport op deze plek. Velden tussen accolades vult GeoVoice per project in.'}</p>
          </div>
          <span class="tag ${s.variant ? 't-purple' : 't-grey'}">${s.variant ? n(s.texts.length, 'versie', 'versies') : 'Vaste tekst'}</span>
        </div>
        ${s.texts.map((t) => {
          const on = t.key === this._key;
          return html`
            <div class="vcard ${on ? 'on' : ''}" @click=${() => { if (!on) this._pickText(t); }}>
              ${t.kind === 'variant' ? html`<span class="when">Gebruikt als <b>${describeRule(t.rule)}</b></span>` : ''}
              <span class="vname">${t.label}${t.custom ? html`<span class="tag t-amber">aangepast</span>` : ''}</span>
              ${on ? this._renderTextEditor(t, canEdit) : html`<div class="vtext">${t.body}</div>`}
            </div>`;
        })}
      </div>`;
  }

  _renderTextEditor(t, canEdit) {
    const facts = [...new Set([...factsInText(t.defaultBody || ''), ...factsInText(this._draft.body), 'project.name'])]
      .filter((f) => REPORT_TEXT_FACTS[f]);
    // Alleen met velden ({{…}}) is er iets anders te zien dan wat er al staat.
    const hasFields = factsInText(this._draft.body).length > 0;
    const asText = hasFields && this._asText;
    return html`
      ${hasFields ? html`
        <div class="viewseg" @click=${(e) => e.stopPropagation()}>
          <button class=${asText ? '' : 'on'} @click=${() => { this._asText = false; }}>${canEdit ? 'Bewerken' : 'Met velden'}</button>
          <button class=${asText ? 'on' : ''} @click=${() => { this._asText = true; }}>Als tekst</button>
          ${asText ? html`<span>velden ingevuld met voorbeelddata</span>` : ''}
        </div>` : ''}
      ${asText ? this._renderAsText(this._draft.body) : html`
        <textarea class="body" .value=${this._draft.body} ?disabled=${!canEdit} rows=${Math.max(4, Math.ceil(this._draft.body.length / 90) + 1)}
                  @input=${(e) => { this._draft = { ...this._draft, body: e.target.value }; }}></textarea>`}
      ${canEdit && !asText ? html`
        <div class="insert">Invoegen:
          ${facts.map((f) => html`<button title=${`${REPORT_TEXT_FACTS[f].label} · bijv. ${REPORT_TEXT_FACTS[f].example}`} @click=${() => this._insert(`{{${f}}}`)}>{{${f}}}</button>`)}
          <select class="seg" @change=${(e) => { if (e.target.value) this._insert(`{{${e.target.value}}}`); e.target.value = ''; }}>
            <option value="">meer…</option>
            ${Object.entries(REPORT_TEXT_FACTS).filter(([k]) => !facts.includes(k)).map(([k, v]) => html`<option value=${k}>${v.label}</option>`)}
          </select>
          <button class="op" @click=${() => this._insert(`${this._draft.body.endsWith('\n') || !this._draft.body ? '' : '\n'}- `)}>– opsomming</button>
        </div>
        <div class="vact">
          <button class="btn primary" ?disabled=${this._busy || !this._draftChanged} @click=${(e) => { e.stopPropagation(); this._saveText(); }}>Opslaan</button>
          ${this._draftChanged ? html`<button class="btn ghost" @click=${(e) => { e.stopPropagation(); this._pickText(t); }}>Annuleren</button>` : ''}
          ${t.custom && t.defaultBody != null ? html`<button class="btn ghost" ?disabled=${this._busy} @click=${(e) => { e.stopPropagation(); this._resetText(); }}>Terug naar standaardtekst</button>` : ''}
        </div>` : ''}
      ${t.kind === 'variant' ? html`
        <details class="rule">
          <summary>Technische regel</summary>
          <p>Wanneer GeoVoice deze versie kiest, in de regeltaal van GeoVoice. Meestal hoef je hier niets aan te doen.</p>
          <textarea .value=${this._draft.rule} ?disabled=${!canEdit} placeholder="Geen regel: altijd van toepassing"
                    @input=${(e) => { this._draft = { ...this._draft, rule: e.target.value }; }}></textarea>
        </details>` : ''}`;
  }

  /** De tekst zoals hij in een rapport komt, met voorbeeldwaarden voor de velden. */
  _renderAsText(text) {
    const lines = String(text).split('\n').filter((l) => l.trim());
    const seg = (s) => s.split(/(\{\{\s*[\w.]+\s*\}\})/).filter(Boolean).map((part) => {
      const m = /^\{\{\s*([\w.]+)\s*\}\}$/.exec(part);
      if (!m) return part;
      const f = REPORT_TEXT_FACTS[m[1]];
      return f ? html`<span class="ex">${f.example}</span>` : html`<span class="ex bad" title="Onbekend veld">${part}</span>`;
    });
    return html`
      <div class="astext">
        ${lines.map((l) => {
          const b = /^\s*[-–]\s+(.*)$/.exec(l);
          return b ? html`<p class="bullet">• ${seg(b[1])}</p>` : html`<p>${seg(l)}</p>`;
        })}
      </div>`;
  }

  // ── Document opmaak ──────────────────────────────────────────────────────

  _renderLayout() {
    const t = this._tpl;
    const edit = this.canEdit && !t.builtIn;
    return html`
      <div class="llayout">
        <div class="card pv">
          <div class="pvhead">
            <b>Voorbeeld</b>
            <button class="seg ${this._showFields ? 'on' : ''}" @click=${() => { this._showFields = !this._showFields; this._renderedFor = null; this.requestUpdate(); }}>
              <span class="dotc"></span>Samenvoegvelden tonen</button>
            ${[['cover', 'Voorblad'], ['toc', 'Inhoud'], ['body', 'Tekstpagina']].map(([k, l]) => html`
              <button class="seg ${this._page === k ? 'on' : ''}" @click=${() => this._goPage(k)}>${l}</button>`)}
          </div>
          <div class="stage ${this._showFields ? 'fields' : ''}">
            <div class="docx-styles"></div>
            <div class="docx-holder"></div>
            ${this._previewState === 'loading' ? html`<div class="stagemsg">Word-bestand laden…</div>` : ''}
            ${this._previewState === 'error' ? html`<div class="stagemsg">Het voorbeeld kon niet worden getoond. Download het bestand om het in Word te bekijken.</div>` : ''}
          </div>
          <div class="pvfoot">${this._showFields ? 'Het Word-bestand zelf, met de samenvoegvelden' : 'Een voorbeeldrapport op dit sjabloon, met verzonnen gegevens'}</div>
        </div>
        <div class="side">
          <div class="card filecard">
            <div class="filehead">
              <span class="docico">DOCX</span>
              <div>
                <b>${t.hasFile ? t.fileName : 'Standaardformat GeoVoice'}</b>
                <small>${t.hasFile
                  ? `Eigen opmaak · geüpload ${t.uploadedAt ? formatDate(t.uploadedAt) : ''}${t.uploadedBy ? ` door ${t.uploadedBy}` : ''}`
                  : t.builtIn ? 'Standaardopmaak van GeoVoice. Dupliceer dit sjabloon om een eigen Word-bestand te gebruiken.'
                  : 'Standaardopmaak van GeoVoice. Upload een eigen Word-bestand voor de huisstijl van je bureau.'}</small>
              </div>
            </div>
            <div class="fileact">
              ${edit ? html`<button class="btn primary" ?disabled=${this._busy} @click=${() => { this._uploadOpen = true; }}>${t.hasFile ? 'Nieuwe versie uploaden' : 'Eigen opmaak uploaden'}</button>` : ''}
              <button class="btn" ?disabled=${this._busy} @click=${() => this._download()}>Downloaden</button>
              ${edit && t.hasFile ? html`<button class="quiet" ?disabled=${this._busy} @click=${() => this._removeFile()}>Terug naar standaard</button>` : ''}
            </div>
          </div>
          ${t.hasFile && t.inspection ? this._renderChecks(t.inspection) : ''}
        </div>
      </div>`;
  }

  _renderChecks(ins) {
    const missing = ins.knownMissing || [];
    const loc = ins.fieldLocations || {};
    const rows = [...missing.map((f) => ({ f, miss: true })), ...(ins.knownFound || []).map((f) => ({ f, miss: false }))];
    const anchors = ins.anchors || [];
    const levels = ins.headingLevels || [];
    const unknown = ins.unknownMergeFields || [];
    return html`
      <div class="card checks">
        <div class="ch"><b>Controle sjabloon</b>
          <span style=${missing.length ? 'color:var(--mv-warning-text)' : 'color:var(--mv-primary)'}>${missing.length ? `${n(missing.length, 'punt', 'punten')} om na te kijken` : 'Alles in orde'}</span></div>
        <span class="caps">Samenvoegvelden · ${(ins.knownFound || []).length} van ${this._known.length} gevonden</span>
        <div class="flist">
          ${rows.map(({ f, miss }) => html`
            <div class="frow ${miss ? 'miss' : ''}">
              <span class=${miss ? 'warndot' : 'okdot'}>${miss ? '!' : '✓'}</span>
              <code>«${f}»</code>
              <span>${miss ? 'Niet gevonden' : (loc[f] || []).join(', ')}</span>
            </div>`)}
        </div>
        <span class="cnote">Ontbrekende velden worden in het rapport niet ingevuld. Dat is geen probleem als je ze niet nodig hebt.</span>
        ${unknown.length ? html`<div class="cline"><span class="warndot">!</span><span>${n(unknown.length, 'onbekend veld', 'onbekende velden')} blijft leeg
          <small>${unknown.map((f) => `«${f}»`).join(', ')}</small></span></div>` : ''}
        ${(ins.highlighted || []).length ? html`<div class="cline"><span class="warndot">!</span><span>Geel gemarkeerde voorbeeldtekst blijft staan
          <small>Op het voorblad of in de kop- of voettekst. Maak er een samenvoegveld of [onderwerp] van als het per rapport verschilt: ${ins.highlighted.map((t) => `‘${t}’`).join(', ')}</small></span></div>` : ''}
        <div class="cline"><span class=${anchors.length ? 'okdot' : 'okdot'}>✓</span><span>${anchors.length
          ? `${n(anchors.length, 'paragraafcode', 'paragraafcodes')} gevonden` : 'Geen paragraafcodes: hoofdstukken komen in de standaardvolgorde'}
          ${anchors.length ? html`<small>${anchors.map((a) => `{{section:${a}}}`).join(', ')}</small>` : ''}</span></div>
        <div class="cline"><span class=${levels.includes(1) ? 'okdot' : 'warndot'}>${levels.includes(1) ? '✓' : '!'}</span><span>${levels.length
          ? `Stijlen Kop ${levels[0]} t/m Kop ${levels[levels.length - 1]} aanwezig` : 'Geen kopstijlen gevonden'}<small>worden gebruikt voor hoofdstukken</small></span></div>
      </div>`;
  }

  _renderUpload() {
    const t = this._tpl;
    const pick = () => this.renderRoot.querySelector('#docxin')?.click();
    return html`
      <div class="overlay" @click=${() => { if (!this._busy) this._uploadOpen = false; }}>
        <div class="modal" role="dialog" aria-modal="true" aria-label="Eigen Word-sjabloon gebruiken" @click=${(e) => e.stopPropagation()}>
          <div class="mhead">
            <div>
              <h3>${t.hasFile ? 'Nieuwe versie uploaden' : 'Eigen Word-sjabloon gebruiken'}</h3>
              <p>Voorblad, kop- en voetteksten, stijlen en logo blijven zoals in jouw Word-bestand. GeoVoice vult de samenvoegvelden en schrijft de hoofdstukken.</p>
            </div>
            <button class="x" title="Sluiten" @click=${() => { this._uploadOpen = false; }}>✕</button>
          </div>
          <div class="steps">
            <div class="step"><i>1</i><b>Download het standaardsjabloon</b>Of begin met een eigen Word-bestand van je bureau.</div>
            <div class="step"><i>2</i><b>Zet de samenvoegvelden</b>Plaats de velden hieronder waar de projectgegevens moeten komen.</div>
            <div class="step"><i>3</i><b>Upload en controleer</b>We controleren het bestand en laten zien wat we vonden.</div>
          </div>
          <div class="drop ${this._dragOver ? 'over' : ''}" role="button" tabindex="0" @click=${pick}
               @keydown=${(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pick(); } }}
               @dragover=${(e) => { e.preventDefault(); this._dragOver = true; }}
               @dragleave=${() => { this._dragOver = false; }}
               @drop=${(e) => { e.preventDefault(); this._dragOver = false; this._uploadFile(e.dataTransfer?.files?.[0]); }}>
            <span class="arrow">↑</span>
            <b>${this._busy ? 'Uploaden en controleren…' : 'Sleep je Word-bestand hierheen'}</b>
            <span>of <em>kies een bestand</em> · .docx, .dotx</span>
            <input id="docxin" type="file" hidden accept=".docx,.dotx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                   @click=${(e) => e.stopPropagation()}
                   @change=${(e) => { const f = e.target.files?.[0]; e.target.value = ''; this._uploadFile(f); }}>
          </div>
          ${this._error ? html`<p class="err">${this._error}</p>` : ''}
          <button class="linkbtn" style="align-self:flex-start" @click=${() => this._download('geovoice')}>↓ Standaardsjabloon downloaden als startpunt</button>
          <div class="mcols">
            <div>
              <h4>Samenvoegvelden</h4>
              <p>In Word: Invoegen › Snelonderdelen › Veld › MergeField. Klik om te kopiëren.</p>
              <div class="flist">
                ${this._known.map((f) => html`
                  <div class="mfrow" @click=${() => this._copy(f)}>
                    <code>«${f}»</code><span>${MERGE_EXAMPLES[f] || ''}</span>
                    <em class=${this._copied === f ? 'ok' : ''}>${this._copied === f ? 'Gekopieerd' : 'Kopieer'}</em>
                  </div>`)}
              </div>
            </div>
            <div>
              <h4>Zelf bepalen waar een paragraaf komt</h4>
              <p>Zet een losse regel met de paragraafcode in je bestand. Paragrafen zonder code komen in de standaardvolgorde.</p>
              <div class="example"><small>Voorbeeld in Word</small>3 Onderzoeksmethode<br><span class="code">{{section:3.2}}</span><br>Eigen tekst van je bureau …</div>
            </div>
          </div>
        </div>
      </div>`;
  }
}

customElements.define('report-settings-page', ReportSettingsPage);
